import { CreativeOSFeatures } from '@/_shared/subscriptionPlans'
import showToastNotification from '@/hooks/useShowToast'
import useUserPermissions from '@/hooks/useUserPermissions'
import { trpc } from '@/utils/trpc'
import { Plus } from '@mynaui/icons-react'
import { useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import AccessControl from './AccessControl'
import { CreateTeamDialog } from './CreateTeamDialog'
import { WorkOSUser } from './UserProfileSettingDetail'
import { PopoverContent, Text } from './custom-components'
import { Loader } from './custom-components/Loader'
import { LockTagSmall } from './custom-components/LockTag'
import { Popover, PopoverTrigger } from './shadcn/popover'

export function UserAccountSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false)
  const { userTeams, authUser, isFetchingAuthUser } = useUserPermissions()
  const [user, set_user] = useLocalStorage<WorkOSUser | undefined>('_wos_user', authUser)
  const { mutate: switchAccount, isPending: isSwitchingAccount } = trpc.switchAccount.useMutation({
    onSuccess: async (result) => {
      if (user && result.mode && result.teamId) {
        set_user({
          ...user,
          mode: result.mode,
          teamId: result.teamId,
        })
      } else if (user && result.mode) {
        set_user({
          ...user,
          mode: result.mode,
          teamId: null,
        })
      }
      showToastNotification('success', {
        message: `Success`,
        description: `Switched account successfully.`,
      })
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    },
    onError: (error) => {
      showToastNotification('error', {
        message: `Error switching account.`,
        description: `${error.message}`,
      })
    },
  })

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen)
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(newValue: boolean) => {
        handleOpenChange(newValue)
      }}
    >
      <CreateTeamDialog open={createTeamDialogOpen} onOpenChange={() => setCreateTeamDialogOpen(false)} />
      <PopoverTrigger asChild>
        <div className={'flex justify-start items-center gap-1.5 cursor-pointer w-full'}>
          {authUser ? (
            <div className={'flex w-6 h-6'}>
              <img
                alt={''}
                src={authUser?.avatar ?? '/bg_gradient.jpg'}
                className={'rounded-full cursor-pointer shrink-0 bg-thememuted w-6 h-6'}
              />
            </div>
          ) : (
            <div className="hidden lg:flex">
              <img alt={'CreativeOS'} src={'/images/logo-full-dark_mode.png'} className={'h-5 dark:block hidden'} />
              <img alt={'CreativeOS'} src={'/images/logo-full-light_mode.png'} className={'h-5 dark:hidden block'} />
            </div>
          )}
          {!isFetchingAuthUser && authUser ? (
            <Text
              size="xs"
              weight={'medium'}
              className={'w-[8.625rem] h-full truncate overflow-ellipsis overflow-hidden'}
            >
              {`${
                authUser?.firstName || authUser?.lastName
                  ? `${authUser.firstName ?? ''} ${authUser.lastName ?? ''}`
                  : authUser?.email
              }`.trim()}
            </Text>
          ) : null}
        </div>
      </PopoverTrigger>
      {isSwitchingAccount ? (
        <PopoverContent
          className="border-none w-56 h-56 px-4 pt-1 pb-4 rounded-2xl ml-4 mt-4 shadow-[0px_4px_40px_0px_#1614143D] gap-3 flex flex-col justify-center items-center"
          align="end"
        >
          <Loader />
        </PopoverContent>
      ) : (
        <PopoverContent
          className="border-none w-56 px-4 pt-1 pb-4 rounded-2xl ml-4 mt-4 shadow-[0px_4px_40px_0px_#1614143D] gap-3 flex flex-col"
          align="end"
        >
          <Text weight={'medium'} size={'sm'} className="my-1">
            Switch Account
          </Text>

          {authUser ? (
            <>
              <div
                className={`flex gap-1.5 items-center cursor-pointer ${user?.mode != 'team' || isSwitchingAccount ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
                onClick={() => {
                  switchAccount({ teamId: undefined })
                }}
              >
                <img
                  alt={''}
                  src={authUser?.avatar ?? '/bg_gradient.jpg'}
                  className={'rounded-full cursor-pointer shrink-0 bg-thememuted w-6 h-6'}
                />
                <div className="flex flex-col gap-0.5">
                  <Text weight={'medium'} size={'sm'}>
                    {`${authUser.firstName} ${authUser.lastName}`}
                  </Text>
                  <Text weight={'normal'} className="text-[10px]">
                    Personal Account
                  </Text>
                </div>
              </div>

              <hr className={'border-themeinput'} />
            </>
          ) : null}
          {userTeams && userTeams.length > 0
            ? userTeams.map((team) => (
                <>
                  <div
                    className={`flex gap-1.5 items-center cursor-pointer ${(user?.mode == 'team' && team.id == user?.teamId) || isSwitchingAccount ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
                    onClick={() => {
                      switchAccount({ teamId: team.id })
                    }}
                  >
                    <img
                      alt={''}
                      src={'/bg_gradient.jpg'}
                      className={'rounded-full cursor-pointer shrink-0 bg-thememuted w-6 h-6'}
                    />

                    <div className="flex flex-col gap-0.5">
                      <Text weight={'medium'} size={'sm'}>
                        {team.name}
                      </Text>
                      <Text weight={'normal'} className="text-[10px]">
                        Team Account
                      </Text>
                    </div>
                  </div>
                  <hr className={'border-themeinput'} />
                </>
              ))
            : null}
          <AccessControl
            feature={CreativeOSFeatures.CREATE_TEAM}
            FeatureUnlocked={
              <div
                onClick={() => {
                  if (!userTeams) return

                  if ((userTeams.length ?? 0) > 0) {
                    showToastNotification('error', {
                      message: 'Action Disabled',
                      description:
                        'You already belong to a team. Kindly switch to your team account or leave your current team to create a new one.',
                    })
                    return
                  }

                  setCreateTeamDialogOpen(true)
                }}
                className={`flex items-center gap-2 cursor-pointer ${
                  isSwitchingAccount || !userTeams || (userTeams?.length ?? 0) > 0 ? 'opacity-50' : 'opacity-100'
                }  ${isSwitchingAccount || !userTeams ? 'pointer-events-none' : ''}`}
              >
                <div className="rounded-full w-6 h-6 min-w-6 bg-themedestructive flex justify-center items-center">
                  <Plus className="w-5 h-5 text-themebackground" />
                </div>
                <Text weight={'medium'} size={'sm'}>
                  Create a Team
                </Text>
              </div>
            }
            FeatureLocked={
              <div className="flex items-center gap-2 cursor-pointer opacity-50 justify-between">
                <div className="flex gap-2 items-center">
                  <div className="rounded-full w-6 h-6 min-w-6 bg-themedestructive flex justify-center items-center">
                    <Plus className="w-5 h-5 text-themebackground" />
                  </div>{' '}
                  <Text weight={'medium'} size={'sm'}>
                    Create a Team
                  </Text>
                </div>
                <LockTagSmall />
              </div>
            }
          />
        </PopoverContent>
      )}
    </Popover>
  )
}
